import React, { useContext, useState } from "react";
import { AuthContext } from "@/App";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { translate } from "@/utils/translations";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  User,
  Bell,
  Shield,
  Palette,
  Globe,
  LogOut,
  Key,
  Trash2,
  Save
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Settings = () => {
  const { user, setUser } = useContext(AuthContext);
  const { language, setLanguage } = useLanguage();
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    notifications: true,
    emailAlerts: true,
    soundEnabled: false,
    theme: "dark",
    language: language || "en"  // Use global language
  });
  
  const t = (text) => translate(text, language);

  const handleRoleChange = async (newRole) => {
    if (isDemo) {
      toast.info("Role change disabled in demo mode");
      return;
    }

    try {
      const res = await fetch(`${API}/auth/role`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ role: newRole })
      });

      if (res.ok) {
        const updatedUser = await res.json();
        setUser(updatedUser);
        toast.success("Role updated successfully");
      }
    } catch (e) {
      toast.error("Failed to update role");
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API}/auth/logout`, {
        method: "POST",
        credentials: "include"
      });
    } catch (e) {
      console.error("Logout error:", e);
    }
    toast.success("Logged out successfully");
    navigate("/");
  };

  const handleSave = () => {
    setSaving(true);
    setTimeout(() => {
      setSaving(false);
      toast.success("Settings saved");
    }, 500);
  };

  const getInitials = (name) => {
    if (!name) return "U";
    return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="font-heading text-2xl lg:text-3xl font-bold text-white">{t("Settings")}</h1>
        <p className="text-gray-500 text-sm mt-1">
          {t("Manage your account and preferences")}
        </p>
      </div>

      {/* Profile section */}
      <Card className="bg-[#0A0A0A] border-white/10">
        <CardHeader>
          <CardTitle className="font-heading text-white flex items-center gap-2">
            <User className="w-5 h-5 text-[#00D2FF]" />
            {t("Profile")}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center gap-6">
            <Avatar className="w-20 h-20">
              <AvatarImage src={user?.picture} />
              <AvatarFallback className="bg-[#00D2FF]/20 text-[#00D2FF] text-xl">
                {getInitials(user?.name)}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-white">{user?.name}</h3>
              <p className="text-gray-500 text-sm">{user?.email}</p>
              <Badge className="mt-2 bg-[#00D2FF]/10 text-[#00D2FF] border-[#00D2FF]/20">
                {user?.role?.toUpperCase() || 'DEVELOPER'}
              </Badge>
            </div>
          </div>

          <Separator className="bg-white/10" />

          <div className="grid gap-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-400">{t("Display Name")}</Label>
                <Input 
                  value={user?.name || ''} 
                  disabled
                  className="bg-[#030303] border-white/10 text-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-400">{t("Email")}</Label>
                <Input 
                  value={user?.email || ''} 
                  disabled
                  className="bg-[#030303] border-white/10 text-white"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-gray-400">{t("Role")}</Label>
              <Select value={user?.role || 'developer'} onValueChange={handleRoleChange}>
                <SelectTrigger className="w-full bg-[#030303] border-white/10" data-testid="role-selector">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#0A0A0A] border-white/10">
                  <SelectItem value="developer" className="text-white hover:bg-white/5">Developer</SelectItem>
                  <SelectItem value="operator" className="text-white hover:bg-white/5">Operator</SelectItem>
                  <SelectItem value="researcher" className="text-white hover:bg-white/5">Researcher</SelectItem>
                  <SelectItem value="enterprise" className="text-white hover:bg-white/5">Enterprise</SelectItem>
                  <SelectItem value="validator" className="text-white hover:bg-white/5">Validator</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-600">Your role determines access to certain features</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Notifications */}
      <Card className="bg-[#0A0A0A] border-white/10">
        <CardHeader>
          <CardTitle className="font-heading text-white flex items-center gap-2">
            <Bell className="w-5 h-5 text-[#00D2FF]" />
            {t("Notifications")}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white font-medium">{t("Push Notifications")}</p>
              <p className="text-sm text-gray-500">{t("Receive real-time alerts in the console")}</p>
            </div>
            <Switch 
              checked={settings.notifications}
              onCheckedChange={(v) => setSettings({...settings, notifications: v})}
            />
          </div>
          <Separator className="bg-white/10" />
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white font-medium">{t("Email Alerts")}</p>
              <p className="text-sm text-gray-500">{t("Critical alerts sent to your email")}</p>
            </div>
            <Switch 
              checked={settings.emailAlerts}
              onCheckedChange={(v) => setSettings({...settings, emailAlerts: v})}
            />
          </div>
          <Separator className="bg-white/10" />
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white font-medium">{t("Sound Effects")}</p>
              <p className="text-sm text-gray-500">{t("Audio feedback for notifications")}</p>
            </div>
            <Switch 
              checked={settings.soundEnabled}
              onCheckedChange={(v) => setSettings({...settings, soundEnabled: v})}
            />
          </div>
        </CardContent>
      </Card>

      {/* Appearance */}
      <Card className="bg-[#0A0A0A] border-white/10">
        <CardHeader>
          <CardTitle className="font-heading text-white flex items-center gap-2">
            <Palette className="w-5 h-5 text-[#00D2FF]" />
            {t("Appearance")}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-gray-400">{t("Theme")}</Label>
              <Select value={settings.theme} onValueChange={(v) => setSettings({...settings, theme: v})}>
                <SelectTrigger className="bg-[#030303] border-white/10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#0A0A0A] border-white/10">
                  <SelectItem value="dark" className="text-white hover:bg-white/5">{t("Dark (Default)")}</SelectItem>
                  <SelectItem value="light" disabled className="text-gray-600">Light (Coming Soon)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-gray-400">{t("Language")}</Label>
              <Select 
                value={language} 
                onValueChange={(v) => {
                  setLanguage(v);
                  setSettings({...settings, language: v});
                  window.location.reload();
                }}
              >
                <SelectTrigger className="bg-[#030303] border-white/10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#0A0A0A] border-white/10">
                  <SelectItem value="en">English</SelectItem>
                  <SelectItem value="zh">中文</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Security */}
      <Card className="bg-[#0A0A0A] border-white/10">
        <CardHeader>
          <CardTitle className="font-heading text-white flex items-center gap-2">
            <Shield className="w-5 h-5 text-[#00D2FF]" />
            {t("Security")}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 glass rounded-lg">
            <div className="flex items-center gap-3">
              <Key className="w-5 h-5 text-gray-400" />
              <div>
                <p className="text-white font-medium">API Keys</p>
                <p className="text-sm text-gray-500">Manage your API access tokens</p>
              </div>
            </div>
            <Button variant="outline" className="border-white/10 hover:bg-white/5">
              Manage
            </Button>
          </div>
          <div className="flex items-center justify-between p-4 glass rounded-lg">
            <div className="flex items-center gap-3">
              <Globe className="w-5 h-5 text-gray-400" />
              <div>
                <p className="text-white font-medium">Active Sessions</p>
                <p className="text-sm text-gray-500">View and manage logged in devices</p>
              </div>
            </div>
            <Badge className="bg-[#00FF94]/10 text-[#00FF94] border-[#00FF94]/20">
              1 Active
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex items-center justify-between">
        <Button 
          variant="outline" 
          className="border-red-500/30 text-red-400 hover:bg-red-500/10"
          onClick={handleLogout}
          data-testid="logout-btn"
        >
          <LogOut className="w-4 h-4 mr-2" />
          Logout
        </Button>

        <Button 
          onClick={handleSave}
          disabled={saving}
          className="bg-[#00D2FF] hover:bg-[#00D2FF] text-black font-bold"
        >
          {saving ? (
            <span className="flex items-center gap-2">
              <span className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
              Saving...
            </span>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Save Changes
            </>
          )}
        </Button>
      </div>

      {/* Danger zone */}
      <Card className="bg-[#0A0A0A] border-red-500/30">
        <CardHeader>
          <CardTitle className="font-heading text-red-400 flex items-center gap-2">
            <Trash2 className="w-5 h-5" />
            {t("Danger Zone")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white font-medium">Delete Account</p>
              <p className="text-sm text-gray-500">Permanently delete your account and all data</p>
            </div>
            <Button variant="outline" className="border-red-500/30 text-red-400 hover:bg-red-500/10">
              Delete Account
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Settings;
